import React, { useState, useEffect, useRef, useMemo } from 'react';
import api from '../../api/axiosConfig';
import './CarpetaPersonal.css';
import FilePreviewModal from '../FilePreviewModal/FilePreviewModal';
import UploadZone from '../ZonaDeSubida/ZonaDeSubida';
import { detectarTabKey } from '../../config/folderRoutes';

const n = (name) => name?.replace(/^\d{2}\s*-\s*/, '') || name;

const getFileIcon = (filename) => {
  if (!filename) return 'bi bi-file-earmark';
  const parts = filename.split('.');
  if (parts.length === 1) return 'bi bi-file-earmark';
  const extension = parts.pop().toLowerCase();
  switch (extension) {
    case 'pdf': return 'bi bi-filetype-pdf';
    case 'doc':
    case 'docx': return 'bi bi-filetype-docx';
    case 'xls':
    case 'xlsx':
    case 'csv': return 'bi bi-filetype-xlsx';
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'gif': return 'bi bi-filetype-jpg';
    case 'zip':
    case 'rar': return 'bi bi-file-zip';
    default: return 'bi bi-file-earmark';
  }
};

const getFileIconColor = (filename) => {
  if (!filename) return '#718096';
  const parts = filename.split('.');
  if (parts.length === 1) return '#718096';
  const extension = parts.pop().toLowerCase();
  switch (extension) {
    case 'pdf': return '#e53e3e';
    case 'doc':
    case 'docx': return '#2b6cb0';
    case 'xls':
    case 'xlsx':
    case 'csv': return '#38a169';
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'gif': return '#805ad5';
    case 'zip':
    case 'rar': return '#d69e2e';
    default: return '#718096';
  }
};

const SORT_OPTIONS = [
  { value: 'default', label: 'Por defecto' },
  { value: 'fecha-desc', label: 'Más reciente' },
  { value: 'nombre-asc', label: 'Nombre A - Z' },
  { value: 'nombre-desc', label: 'Nombre Z - A' },
  { value: 'tipo', label: 'Carpetas primero' },
];

const SORT_STORAGE_KEY = 'carpetaPersonal_sortOrder';

const CarpetaPersonal = ( { crearNotificacion, notificaciones, carpetaId, nombreCliente, isAdmin, startPath, entidadCuit, entidadId, addToast, tabKey, targetFolderId, targetFolderPath, carpetasConNovedades } ) => {
  const [history, setHistory] = useState([]);
  const [carpetaActual, setCarpetaActual] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [archivoAEliminar, setArchivoAEliminar] = useState(null);
  const [elementosOcultos, setElementosOcultos] = useState([]);

  const [sortOrder, setSortOrder] = useState(() => {
    return localStorage.getItem(SORT_STORAGE_KEY) || 'default';
  });

  const [refreshing, setRefreshing] = useState(false);

  const handleSortChange = (e) => {
    const newOrder = e.target.value;
    setSortOrder(newOrder);
    localStorage.setItem(SORT_STORAGE_KEY, newOrder);
  };

  const handleRefresh = async () => {
    if (!carpetaActual) return;
    setRefreshing(true);
    setError('');
    try {
      const response = await api.get(`/obtenerCarpetaPorId?carpetaId=${carpetaActual.id}&nocache=true`);
      const data = response.data;
      setCarpetaActual(prev => ({
        ...prev,
        id: data.folderId || carpetaActual.id,
        contenido: data.contenido_actual || []
      }));
    } catch (err) {
      console.error("Error al refrescar la carpeta", err);
      setError('No se pudo actualizar el contenido de la carpeta.');
    } finally {
      setRefreshing(false);
    }
  };

  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewFile, setPreviewFile] = useState(null);

  const historyRef = useRef(history);

  useEffect(() => {
    historyRef.current = history;
  }, [history]);

  useEffect(() => {
    if (entidadCuit) {
      fetchElementosOcultos(entidadCuit);
    }
  }, [entidadCuit]);

  const abrirRaiz = async (isGoBack = false) => {
    if (!carpetaId) {
      setCarpetaActual(null);
      return;
    }
    
    if (startPath && startPath.length > 0) {
      setLoading(true);
      setError('');
      try {
        let currentId = carpetaId;
        let finalName = nombreCliente ? `Archivos de ${nombreCliente}` : 'Carpeta Personal';
        
        for (const folderName of startPath) {
          const response = await api.get(`/obtenerCarpetaPorId?carpetaId=${currentId}`);
          const data = response.data;
          const folderNameClean = folderName.replace(/^\d{2}\s*-\s*/, '').toLowerCase();
          const subfolder = (data.contenido_actual || []).find(item => {
            if (item.tipo !== 'Carpeta') return false;
            const itemName = item.nombre.toLowerCase();
            return itemName.includes(folderNameClean) || itemName.includes(folderName.toLowerCase());
          });
          
          if (subfolder) {
            currentId = subfolder.id;
            finalName = subfolder.nombre;
          } else {
            const carpetas = (data.contenido_actual || []).filter(i => i.tipo === 'Carpeta');
            console.error('Carpetas disponibles en OneDrive:', carpetas.map(c => ({ nombre: c.nombre, id: c.id })));
            console.error('Buscando folderName:', folderName);
            setCarpetaActual(null);
            setError(`No se encontro "${folderName}". Carpetas: ${carpetas.map(c => c.nombre).join(', ') || '(vacio)'}. Revisa la consola (F12).`);
            setLoading(false);
            return;
          }
        }
        abrirCarpeta(currentId, finalName, isGoBack);
      } catch (err) {
        console.error("Error al resolver la ruta", err);
        setCarpetaActual(null);
        setError('Error al obtener la carpeta.');
        setLoading(false);
      }
    } else {
      abrirCarpeta(carpetaId, nombreCliente ? `Archivos de ${nombreCliente}` : 'Carpeta Personal', isGoBack);
    }
  };

  React.useEffect(() => {
    if (!carpetaActual && history.length === 0) {
      abrirRaiz();
    }
  }, [carpetaId, nombreCliente]);

  const abrirCarpeta = async (id, nombre, isGoBack = false) => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`/obtenerCarpetaPorId?carpetaId=${id}`);
      const data = response.data;
      
      if (!isGoBack && carpetaActual) {
        const nextHistory = [...history, { id: carpetaActual.id, nombre: carpetaActual.nombre }];
        setHistory(nextHistory);
      }

      setCarpetaActual({
        id: data.folderId || id,
        nombre: nombre,
        contenido: data.contenido_actual || []
      });
    } catch (err) {
      console.error("Error al obtener la carpeta", err);
      if (!isGoBack && carpetaActual) {
        const nextHistory = [...history, { id: carpetaActual.id, nombre: carpetaActual.nombre }];
        setHistory(nextHistory);
      }
      setCarpetaActual({
        id: id,
        nombre: nombre,
        contenido: []
      });
      setError('No se pudo cargar el contenido desde el servidor. Mostrando carpeta vacia.');
    } finally {
      setLoading(false);
      fetchElementosOcultos(entidadCuit);
    }
  };

  const handleVolver = () => {
    if (history.length > 0) {
      const prev = history[history.length - 1];
      setHistory(prev => prev.slice(0, -1));
      abrirCarpeta(prev.id, prev.nombre, true);
    }
  };

  const handleItemClick = (item) => {
    if (item.tipo === 'Carpeta') {
      abrirCarpeta(item.id, item.nombre);
    } else {
      handlePrevisualizarArchivo(item);
    }
  };

  const handleSubirArchivo = async (e) => {
    const files = e.target ? e.target.files : e;
    if (!files || files.length === 0 || !carpetaActual) return;

    let file = files[0];
    const nombresExistentes = carpetaActual.contenido
      .filter(item => item.tipo === 'Archivo')
      .map(item => item.nombre);

    let nombreFinal = file.name;
    if (nombresExistentes.includes(nombreFinal)) {
      const base = file.name.replace(/(\.[^.]+)$/, '');
      const ext = file.name.match(/(\.[^.]+)$/)?.[1] || '';
      let counter = 1;
      while (nombresExistentes.includes(`${base} (${counter})${ext}`)) {
        counter++;
      }
      nombreFinal = `${base} (${counter})${ext}`;
      file = new File([file], nombreFinal, { type: file.type });
    }

    const formData = new FormData();
    formData.append('archivo', file);

    setLoading(true);
    try {
      await api.post(`/subirArchivo?carpetaId=${carpetaActual.id}`, formData);
      if (addToast) addToast(`Archivo subido: ${nombreFinal}`, 'success');
      if (crearNotificacion) {
        crearNotificacion([
          ...notificaciones,
          { id: Date.now(), mensaje: `Nuevo archivo subido: ${nombreFinal.slice(0, 20)}`, tiempo: 'Hace un momento', leida: false }
        ]);
      }
      abrirCarpeta(carpetaActual.id, carpetaActual.nombre, true);
    } catch (err) {
      console.error('Error al subir archivo:', err);
      if (addToast) addToast('Error al subir el archivo', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEliminarClick = (archivo) => {
    setArchivoAEliminar(archivo);
  };

  const confirmarEliminacion = async () => {
    if (!archivoAEliminar) return;
    try {
      await api.delete(`/eliminarArchivo?archivoId=${archivoAEliminar.id}`);
      
      if (addToast) addToast(`Archivo eliminado: ${archivoAEliminar.nombre}`, 'error');
      if (crearNotificacion) {
        crearNotificacion([
          ...notificaciones,
          { id: Date.now(), mensaje: `Archivo eliminado: ${archivoAEliminar.nombre.slice(0, 20)}`, tiempo: 'Hace un momento', leida: false }
        ]);
      }
      
      abrirCarpeta(carpetaActual.id, carpetaActual.nombre, true);
    } catch (err) {
      console.error("Error al eliminar el archivo:", err);
      if (addToast) addToast('Error al eliminar el archivo', 'error');
    } finally {
      setArchivoAEliminar(null);
    }
  };

  const cancelarEliminacion = () => {
    setArchivoAEliminar(null);
  };

  const handleNotificarCliente = async (item) => {
    if (!isAdmin || !carpetaActual) return;
    if (!entidadCuit && !entidadId) {
      if (addToast) addToast('No se puede notificar: selecciona una entidad.', 'warning');
      return;
    }
    try {
      const folderPath = [...history, { id: carpetaActual.id, nombre: carpetaActual.nombre }];
      const effectiveTabKey = tabKey || detectarTabKey(folderPath) || 'carpeta-personal';
      const body = {
        ...(entidadId ? { entidadId } : { cuitUser: entidadCuit }),
        archivoNombre: item.nombre,
        carpetaNombre: n(carpetaActual.nombre),
        carpetaRealId: carpetaActual.id,
        tabKey: effectiveTabKey,
        folderPath
      };
      console.log('Notificar - body:', body);
      await api.post('/notificarArchivoNuevo', body);
      if (addToast) addToast(`Notificado: ${item.nombre}`, 'success');
    } catch (e) {
      console.error('Error al notificar:', e, e?.response?.data);
      if (addToast) addToast(e?.response?.data?.error || 'Error al notificar.', 'warning');
    }
  };

  const targetSeenRef = useRef(null);

  useEffect(() => {
    if (carpetaActual && targetFolderId && targetSeenRef.current !== targetFolderId) {
      targetSeenRef.current = targetFolderId;
      if (carpetaActual.id !== targetFolderId) {
        abrirCarpeta(targetFolderId, '(sin nombre)', false);
      }
    }
  }, [carpetaActual, targetFolderId]);

  useEffect(() => {
    if (carpetaActual && carpetasConNovedades && carpetasConNovedades.includes(carpetaActual.id)) {
      api.post('/marcarCarpetaLeida', { carpetaId: carpetaActual.id }).catch(() => {});
    }
  }, [carpetaActual?.id, carpetasConNovedades]);

  const handleDescargarArchivo = async (archivo) => {
    try {
      const response = await api.get(`/descargarArchivo?archivoId=${archivo.id}`);
      const urlDescarga = response.data.url || response.data.downloadUrl;

      if (urlDescarga) {
        const link = document.createElement('a');
        link.href = urlDescarga;
        link.setAttribute('download', archivo.nombre || 'documento');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        console.error('El servidor no devolvio una URL de descarga valida.');
      }
    } catch (error) {
      console.error("Error al obtener el link de descarga:", error);
    }
  };

  const handlePrevisualizarArchivo = async (archivo) => {
    try {
      setPreviewFile(archivo);
      setPreviewUrl('');
      setPreviewModalOpen(true);
      
      const response = await api.get(`/previsualizarArchivo?archivoId=${archivo.id}`);
      if (response.data && response.data.url) {
        setPreviewUrl(response.data.url);
      } else {
        alert("No se pudo obtener la vista previa del archivo.");
        setPreviewModalOpen(false);
      }
    } catch (error) {
      console.error("Error al obtener la vista previa:", error);
      alert("Hubo un error al intentar abrir la vista previa.");
      setPreviewModalOpen(false);
    }
  };

  const fetchElementosOcultos = async (entidadCuit) => {
    if (!entidadCuit) return;
    try {
      const response = await api.get(`/obtenerElementosOcultos?cuit=${entidadCuit}`);
      if (response.data && response.data.elementosOcultos) {
        setElementosOcultos(response.data.elementosOcultos);
      }
    } catch (error) {
      console.error("Error al obtener elementos ocultos:", error);
    }
  };

  const toggleVisibilidad = async (archivo) => {
    if (!isAdmin || !entidadCuit) return;
    const elementoOculto = elementosOcultos.find(e => e.id === archivo.id);
    const estaOculto = !!elementoOculto;
    const accion = estaOculto ? 'mostrar' : 'ocultar';
    const mensaje = accion === 'ocultar' ? 'ocultado' : 'mostrado';
    try {
      await api.post('/toggleVisibilidadElemento', {
        onedrive_item_id: archivo.id,
        accion: accion,
        cuit: entidadCuit
      });
      await fetchElementosOcultos(entidadCuit);
      if (addToast) addToast(`Archivo ${mensaje}: ${archivo.nombre}`, 'warning');
      if (crearNotificacion) {
        crearNotificacion([
          ...notificaciones,
          { id: Date.now(), mensaje: `Archivo ${mensaje}: ${archivo.nombre.slice(0, 20)}`, tiempo: 'Hace un momento', leida: false }
        ]);
      }
    } catch (error) {
      console.error("Error al togglear visibilidad:", error);
      if (addToast) addToast('Error al cambiar visibilidad', 'error');
    }
  };

  const contenidoMixto = carpetaActual ? carpetaActual.contenido : [];

  const parseFecha = (fechaStr) => {
    if (!fechaStr) return 0;
    const [datePart, timePart] = fechaStr.split(' ');
    const dateParts = datePart.split('/');
    const timeParts = timePart ? timePart.split(':') : [];
    if (dateParts.length === 3) {
      const day = parseInt(dateParts[0], 10);
      const month = parseInt(dateParts[1], 10) - 1;
      let year = parseInt(dateParts[2], 10);
      if (year < 100) year += 2000;
      const hours = parseInt(timeParts[0] || '0', 10);
      const minutes = parseInt(timeParts[1] || '0', 10);
      return new Date(year, month, day, hours, minutes).getTime();
    }
    return 0;
  };

  const sortedContent = useMemo(() => {
    if (!contenidoMixto || !sortOrder || sortOrder === 'default') return contenidoMixto;
    const items = [...contenidoMixto];
    switch (sortOrder) {
      case 'fecha-desc':
        return items.sort((a, b) => parseFecha(b.fecha) - parseFecha(a.fecha));
      case 'nombre-asc':
        return items.sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
      case 'nombre-desc':
        return items.sort((a, b) => b.nombre.localeCompare(a.nombre, 'es'));
      case 'tipo':
        return items.sort((a, b) => {
          if (a.tipo === 'Carpeta' && b.tipo !== 'Carpeta') return -1;
          if (a.tipo !== 'Carpeta' && b.tipo === 'Carpeta') return 1;
          return a.nombre.localeCompare(b.nombre, 'es');
        });
      default:
        return items;
    }
  }, [contenidoMixto, sortOrder]);

  const SkeletonLoader = () => (
    <div className="skeleton-container">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="skeleton-item">
          <div className="skeleton-left">
            <div className="skeleton-icon"></div>
            <div className="skeleton-text-container">
              <div className="skeleton-text-main" style={{ width: `${Math.random() * 30 + 30}%` }}></div>
              <div className="skeleton-text-sub" style={{ width: `${Math.random() * 20 + 15}%` }}></div>
            </div>
          </div>
          <div className="skeleton-right">
            <div className="skeleton-action"></div>
            <div className="skeleton-action"></div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="carpeta-personal-container">
      <div className="carpeta-personal-header">
        {history.length > 0 && (
          <button className="btn-volver" onClick={handleVolver} disabled={loading}>
            <i className="bi bi-arrow-left"></i> Volver ({n(history[history.length - 1].nombre)})
          </button>
        )}
        {carpetaActual ? (
          <h2>{n(carpetaActual.nombre)}</h2>
        ) : (
          <h2>{nombreCliente ? `Archivos de ${nombreCliente}` : 'Carpeta Personal'}</h2>
        )}
        <div className="vista-sort-container">
          <i className="bi bi-sort-down" style={{ color: '#64748b', fontSize: '1.1rem' }}></i>
          <select
            className="vista-sort-select"
            value={sortOrder}
            onChange={handleSortChange}
          >
            {SORT_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <button
            className={`btn-refrescar ${refreshing || loading ? 'refreshing' : ''}`}
            onClick={handleRefresh}
            disabled={refreshing || loading}
            title="Actualizar carpeta"
          >
            <i className={`bi ${refreshing || loading ? 'bi-arrow-clockwise' : 'bi-arrow-repeat'}`}></i>
          </button>
        </div>
      </div>

      {error && (
        <div style={{ backgroundColor: '#fef2f2', color: '#b91c1c', padding: '10px', borderRadius: '6px', marginBottom: '15px', fontSize: '14px' }}>
          <i className="bi bi-exclamation-triangle" style={{ marginRight: 8 }}></i>
          {error}
        </div>
      )}

      {loading ? (
        <div className="carpeta-detalle">
          <div className="carpeta-detalle-header">
            <h3 style={{ color: '#e2e8f0', width: '200px', height: '24px', backgroundColor: '#e2e8f0', borderRadius: '4px', animation: 'pulse 1.5s infinite ease-in-out' }}> </h3>
            <p style={{ color: '#e2e8f0', width: '100px', height: '16px', backgroundColor: '#e2e8f0', borderRadius: '4px', animation: 'pulse 1.5s infinite ease-in-out', marginTop: '8px' }}> </p>
          </div>
          <div className="archivos-list">
            <h4>Contenido</h4>
            <SkeletonLoader />
          </div>
        </div>
      ) : (!carpetaId) ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
          Esta entidad no tiene una carpeta personal asignada.
        </div>
      ) : !carpetaActual ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
          No hay carpeta seleccionada.
        </div>
      ) : (
        <div className="carpeta-detalle">
          <div className="carpeta-detalle-header">
            <h3><i className="bi bi-folder-open" style={{ marginRight: 8, color: '#1a3c5e' }}></i> Explorando: {n(carpetaActual.nombre)}</h3>
            <p>Directorio actual</p>
          </div>

          <UploadZone onSubir={handleSubirArchivo} loading={loading} />

          <div className="archivos-list">
            <h4>Contenido</h4>
            {sortedContent.length === 0 ? (
              <div className="empty-state">La carpeta esta vacia.</div>
            ) : (
              <ul className="lista-items">
                {sortedContent
                  .filter(item => isAdmin || !elementosOcultos.some(e => e.id === item.id))
                  .map((item) => (
                  <li key={item.id} className="archivo-item" style={{ cursor: 'pointer', transition: 'background-color 0.2s' }} onClick={() => handleItemClick(item)}>
                    <div className="archivo-info-izquierda">
                      <span className="file-type-icon">
                        {item.tipo === 'Carpeta' 
                          ? <i className="bi bi-folder" style={{ color: '#2a5a8a', fontSize: '1.5rem' }}></i>
                          : <i className={getFileIcon(item.nombre)} style={{ color: getFileIconColor(item.nombre), fontSize: '1.5rem' }}></i>
                        }
                      </span>
                      <div className="archivo-textos">
                        <div className="archivo-nombre" style={{ 
                          color: item.tipo === 'Carpeta' ? '#2a5a8a' : '#334155', 
                          fontWeight: item.tipo === 'Carpeta' ? '600' : '400'
                        }}>
                          {item.tipo === 'Carpeta' && carpetasConNovedades && carpetasConNovedades.includes(item.id) && (
                            <span style={{ color: '#dc2626', marginRight: 6, fontSize: '10px', verticalAlign: 'super' }}>●</span>
                          )}
                          {n(item.nombre)}
                          {isAdmin && elementosOcultos.some(e => e.id === item.id) && (
                            <span style={{ color: '#b45309', marginLeft: '8px', fontSize: '0.8em' }}>
                              (oculto por {elementosOcultos.find(e => e.id === item.id).ocultor} el {new Date(elementosOcultos.find(e => e.id === item.id).fecha).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' })})
                            </span>
                          )}
                        </div>
                        <div className="archivo-meta">
                          {item.tipo} {item.size ? `- ${item.size}` : ''} {item.fecha ? `- ${item.fecha}` : ''}
                        </div>
                      </div>
                    </div>
                    
                    <div className="archivo-acciones" onClick={(e) => e.stopPropagation()}>
                      {isAdmin && (
                        <button 
                          className="btn-accion btn-visibilidad" 
                          title={elementosOcultos.some(e => e.id === item.id) ? "Mostrar" : "Ocultar"}
                          onClick={() => toggleVisibilidad(item)}
                        >
                          <i className={`bi ${elementosOcultos.some(e => e.id === item.id) ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                        </button>
                      )}
                      {item.tipo === 'Archivo' && (
                        <>
                          <button 
                            className="btn-accion btn-descargar" 
                            title="Descargar"
                            onClick={() => handleDescargarArchivo(item)}
                          >
                            <i className="bi bi-download"></i>
                          </button>
                          {isAdmin && (
                            <button 
                              className="btn-accion btn-notificar" 
                              title="Notificar al cliente"
                              onClick={(e) => { e.stopPropagation(); handleNotificarCliente(item); }}
                            >
                              <i className="bi bi-send"></i>
                            </button>
                          )}
                          {isAdmin && (
                            <button 
                              className="btn-accion btn-eliminar" 
                              title="Eliminar"
                              onClick={() => handleEliminarClick(item)}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
      {archivoAEliminar && (
        <div className="modal-overlay" onClick={cancelarEliminacion}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Estas seguro de eliminar este archivo?</h3>
            <p>Se borrara permanentemente "{archivoAEliminar.nombre}" de OneDrive.</p>
            <div className="modal-actions">
              <button className="btn-cancelar" onClick={cancelarEliminacion}>Cancelar</button>
              <button className="btn-confirmar-eliminar" onClick={confirmarEliminacion}><i className="bi bi-trash"></i> Si, Eliminar</button>
            </div>
          </div>
        </div>
      )}

      <FilePreviewModal 
        isOpen={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        fileUrl={previewUrl}
        fileName={previewFile ? previewFile.nombre : ''}
        onDownload={previewFile ? () => handleDescargarArchivo(previewFile) : null}
      />
    </div>
  );
};

export default CarpetaPersonal;
